import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 코드/명칭 LIKE 제안 (type-ahead)
 * @param {object} config
 * @param {(ctx: { q: string, row?: unknown }) => Array|{then}|Promise} config.getOptions
 * @param {number} [config.minChars=1]
 * @param {number} [config.maxResults=20]
 * @param {number} [config.debounceMs=200]
 * @param {boolean} [config.enabled=true]
 */
export function useCodeSuggest(config) {
  const {
    getOptions,
    minChars = 1,
    maxResults = 20,
    debounceMs = 200,
    enabled = true,
  } = config || {};

  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const reqIdRef = useRef(0);
  const getOptionsRef = useRef(getOptions);
  getOptionsRef.current = getOptions;

  const close = useCallback(() => {
    setIsOpen(false);
    setOptions([]);
    setHighlightIndex(0);
  }, []);

  const open = useCallback(() => {
    if (!enabled) return;
    setIsOpen(true);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      close();
      return undefined;
    }
    const q = String(query ?? '');
    const trimmed = q.trim();
    if (trimmed.length < minChars) {
      setOptions([]);
      setIsOpen(false);
      setLoading(false);
      return undefined;
    }

    const reqId = ++reqIdRef.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const raw = await Promise.resolve(
          getOptionsRef.current?.({ q: trimmed }) ?? []
        );
        if (reqId !== reqIdRef.current) return;
        const list = (Array.isArray(raw) ? raw : []).slice(0, maxResults);
        setOptions(list);
        setHighlightIndex(0);
        setIsOpen(list.length > 0);
      } catch {
        if (reqId !== reqIdRef.current) return;
        setOptions([]);
        setIsOpen(false);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [query, enabled, minChars, maxResults, debounceMs, close]);

  const moveHighlight = useCallback(
    (delta) => {
      if (!options.length) return;
      setHighlightIndex((prev) => {
        const next = prev + delta;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
      setIsOpen(true);
    },
    [options.length]
  );

  /** 입력값으로 확정 후보 1건 해석 (코드 완전일치 우선, 명칭 완전일치) */
  const resolveExact = useCallback(async (value, row) => {
    const t = String(value ?? '').trim();
    if (!t) return { ok: true, option: null };
    const raw = await Promise.resolve(getOptionsRef.current?.({ q: t, row }) ?? []);
    const list = Array.isArray(raw) ? raw : [];
    const lower = t.toLowerCase();
    const byCode = list.filter((o) => String(o.code || '').toLowerCase() === lower);
    if (byCode.length === 1) return { ok: true, option: byCode[0] };
    if (byCode.length > 1) {
      return { ok: false, option: null, message: '일치하는 코드가 여러 건입니다. 목록에서 선택하세요.' };
    }
    const byName = list.filter((o) => String(o.name || '').toLowerCase() === lower);
    if (byName.length === 1) return { ok: true, option: byName[0] };
    if (byName.length > 1) {
      return { ok: false, option: null, message: '일치하는 명칭이 여러 건입니다. 목록에서 선택하세요.' };
    }
    return { ok: false, option: null, message: '등록된 코드/명칭이 없습니다.' };
  }, []);

  return {
    query,
    setQuery,
    options,
    loading,
    isOpen,
    open,
    close,
    highlightIndex,
    setHighlightIndex,
    moveHighlight,
    resolveExact,
    enabled,
  };
}

/**
 * 마스터 행 배열 → SuggestOption[] (LIKE, 코드 선두 우선)
 * @param {object[]} list
 * @param {string} q
 * @param {{ codeField?: string, nameField?: string, idField?: string, maxResults?: number, filterRow?: (r: object) => boolean, extraFields?: string[] }} [opts]
 */
export function filterCodeSuggestOptions(list, q, opts = {}) {
  const {
    codeField = 'code',
    nameField = 'name',
    idField,
    maxResults = 20,
    filterRow,
    extraFields = [],
  } = opts;
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return [];

  const matched = [];
  for (const r of list || []) {
    if (typeof filterRow === 'function' && !filterRow(r)) continue;
    const code = String(r[codeField] ?? r.code ?? '').trim();
    const name = String(r[nameField] ?? r.name ?? '').trim();
    if (!code && !name) continue;
    const codeL = code.toLowerCase();
    const nameL = name.toLowerCase();
    const extraHit = (extraFields || []).some((f) =>
      String(r[f] ?? '')
        .toLowerCase()
        .includes(needle)
    );
    if (!codeL.includes(needle) && !nameL.includes(needle) && !extraHit) continue;
    const id = String(
      (idField && r[idField]) || r.id || r.projectUid || r.deptUid || r.subjectUid || code
    );
    matched.push({
      id,
      code,
      name,
      raw: r,
      _rank: codeL === needle ? 0 : codeL.startsWith(needle) ? 1 : nameL.startsWith(needle) ? 2 : 3,
    });
  }

  matched.sort((a, b) => {
    if (a._rank !== b._rank) return a._rank - b._rank;
    return String(a.code).localeCompare(String(b.code), 'ko');
  });

  return matched.slice(0, maxResults).map(({ _rank, ...rest }) => rest);
}

export default useCodeSuggest;
